import { useQuery } from "@apollo/client";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import { CopyableText } from "components/CopyableText";
import { FormAutocomplete } from "components/Forms/FormAutocomplete";
import { TruncateTypography } from "components/TruncateTypography";
import { gql } from "generated-graphql";
import { CredentialInputForValidation, CredentialKind } from "generated-graphql/graphql";
import { useEffect, useMemo } from "react";
import { Control, useWatch } from "react-hook-form";

// Lean, customer-safe portal list (vs. the staff-only `portalSearch`). Returns
// only the fields the picker needs so non-staff users can select a portal.
const GET_PORTAL_OPTIONS = gql(`
  query PortalPickerOptions($kind: CredentialKind, $includeId: ID) {
    portalPickerOptions(kind: $kind, includeId: $includeId) {
      id
      jurisdiction
      description
      url
    }
  }
`);

type CredentialPortalPickerProps = {
  control: Control<CredentialInputForValidation>;
  facilityState?: string | null;
  kind?: CredentialKind | null;
  disabled?: boolean;
  /**
   * The portal this credential already uses, exempt from both the `kind` filter and the
   * active-only one — the kind→type map is narrower than the data (a `TIERII` credential
   * on an `LEPC` portal is ordinary), and without the exemption the Portal box and its
   * URL render blank. Callers drop it once the type is touched.
   */
  storedPortalId?: string | null;
  /**
   * Called with the selected portal's jurisdiction (or null when nothing is
   * selected / the option isn't loaded). Lets the parent dialog derive the
   * jurisdiction from the already-loaded options instead of a separate
   * staff-only `portal(id)` query.
   */
  onJurisdictionChange?: (jurisdiction: string | null) => void;
};

export const CredentialPortalPicker = ({
  kind,
  disabled,
  control,
  storedPortalId,
  onJurisdictionChange,
}: CredentialPortalPickerProps) => {
  const theme = useTheme();
  const { data } = useQuery(GET_PORTAL_OPTIONS, {
    variables: {
      kind: kind ?? undefined,
      includeId: storedPortalId ?? undefined,
    },
  });

  const portals = useMemo(() => data?.portalPickerOptions ?? [], [data]);

  const portalId = useWatch({ control, name: "portalId" });
  const selectedPortal = useMemo(() => portals.find((p) => p.id === portalId), [portals, portalId]);
  const portalUrl = selectedPortal?.url;

  const portalItems = useMemo(
    () => portals.map((p) => ({ value: p.id, label: p.description ?? p.jurisdiction })),
    [portals]
  );

  useEffect(() => {
    onJurisdictionChange?.(selectedPortal?.jurisdiction ?? null);
  }, [selectedPortal, onJurisdictionChange]);

  return (
    <Stack>
      <FormAutocomplete
        sx={{ mt: 0.5 }}
        disabled={disabled}
        name="portalId"
        label="Portal"
        required
        autocompleteItems={portalItems}
        control={control}
        rules={{ required: true }}
      />

      {portalUrl ? (
        <Box paddingBottom={theme.spacing(2)} display="flex" flexWrap="nowrap">
          <Typography fontWeight="bold" paddingRight={theme.spacing(0.5)}>
            URL:
          </Typography>
          <TruncateTypography
            showTooltip
            sx={{ maxWidth: theme.spacing(50), paddingRight: theme.spacing(0.5) }}
          >
            {portalUrl}
          </TruncateTypography>
          <CopyableText hideText>{portalUrl}</CopyableText>
        </Box>
      ) : null}
    </Stack>
  );
};
